import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { COUNTDOWN_MS, PARTY_MS, endShow, startNewYearClock, useNewYearShow } from "../lib/newYear";
import { realNow } from "../lib/room";
import { playRaw } from "../lib/sounds";
import { NEW_YEAR_SOUNDS } from "../lib/eventSounds";
import { currentLang, useT } from "../i18n";

// The New Year countdown over the whole page (see lib/newYear.js): a disco
// ball drops, ten seconds count down, then fireworks, the new year's number
// and music.
export default function NewYear() {
  const show = useNewYearShow();
  useEffect(startNewYearClock, []);
  return show ? <NewYearShow key={show.from} show={show} /> : null;
}

function NewYearShow({ show }) {
  const t = useT();
  const [now, setNow] = useState(realNow);
  const lastTick = useRef(null);
  const midnight = show.from + COUNTDOWN_MS;
  const left = Math.ceil((midnight - now) / 1000);
  const party = now >= midnight;

  useEffect(() => {
    const id = setInterval(() => {
      const n = realNow();
      setNow(n);
      if (n >= midnight + PARTY_MS) endShow();
    }, 100);
    return () => clearInterval(id);
  }, [midnight]);

  // A tick each second, then the party.
  useEffect(() => {
    const key = party ? "party" : left;
    if (lastTick.current === key || left > 10) return;
    lastTick.current = key;
    playRaw(() => (party ? NEW_YEAR_SOUNDS.midnight() : NEW_YEAR_SOUNDS.tick(left)));
  }, [left, party]);

  const names = show.names?.length ? new Intl.ListFormat(currentLang(), { type: "conjunction" }).format(show.names) : null;
  return (
    <div className={`newyear ${party ? "newyear-party" : ""}`} role="dialog" aria-modal="true" aria-label={t("Happy New Year!")}>
      <div className="newyear-lights" aria-hidden />
      <div className="newyear-ball" aria-hidden>
        <span className="newyear-string" />
        <span className="newyear-disco">
          <HangingBall />
        </span>
      </div>
      {party ? (
        <>
          <Fireworks />
          <div className="newyear-year" aria-live="polite">
            {String(show.year)
              .split("")
              .map((d, i) => (
                <span key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                  {d}
                </span>
              ))}
          </div>
          <p className="newyear-wish">{t("Happy New Year!")}</p>
          {names && <p className="newyear-names">{t("{names} just entered {year}!", { names, year: show.year })}</p>}
        </>
      ) : (
        <div className="newyear-count" key={left} aria-live="assertive">
          {Math.max(1, Math.min(10, left))}
        </div>
      )}
    </div>
  );
}

// The 3D disco ball downloads only when the countdown starts. The flat ball
// below shows while it loads, and instead of it if the device can't draw 3D.
const DiscoBall3D = lazy(() => import("./DiscoBall3D"));
class Fallback extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <DiscoBall /> : this.props.children;
  }
}
function HangingBall() {
  const [failed, setFailed] = useState(false);
  const fail = useCallback(() => setFailed(true), []);
  if (failed) return <DiscoBall />;
  return (
    <Fallback>
      <Suspense fallback={<DiscoBall />}>
        <DiscoBall3D onFail={fail} />
      </Suspense>
    </Fallback>
  );
}

// Bursts of sparks in gold, pink, blue and white, drawn on a canvas.
const COLORS = ["#ffd23f", "#ff5fa2", "#6ad6ff", "#ffffff", "#ffb44d", "#b994ff"];
function Fireworks() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.("2d");
    if (!ctx) return;
    const calm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let sparks = [];
    let frame;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const boom = () => {
      const x = canvas.width * (0.15 + Math.random() * 0.7);
      const y = canvas.height * (0.12 + Math.random() * 0.35);
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const count = calm ? 24 : 60;
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2;
        const v = 2 + Math.random() * 3.5;
        sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1, color });
      }
    };
    boom();
    const bursts = setInterval(boom, calm ? 900 : 450);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      sparks = sparks.filter((s) => s.life > 0);
      for (const s of sparks) {
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.05;
        s.vx *= 0.985;
        s.life -= 0.012;
        ctx.globalAlpha = Math.max(0, s.life);
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      clearInterval(bursts);
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="newyear-fireworks" aria-hidden />;
}

// The big disco ball, in 2.5D: a cartoon ball (chunky outline, cap, big
// shine) covered in mirror tiles that really wrap around a sphere. Each tile
// is lit on its own, bright towards the top left and dark underneath, and
// the ball slowly spins so the tiles slide round and flash as they catch the
// light.
const BALL_R = 46;
const LIGHT = (() => {
  const l = [-0.5, -0.6, 0.62];
  const n = Math.hypot(...l);
  return l.map((v) => v / n);
})();
const LAT_STEP = 15;
const LON_STEP = 18;
const HUES = [205, 265, 190, 230, 280];

// The tiles that face us when the ball has turned by `spin` degrees.
function ballTiles(spin) {
  const rad = Math.PI / 180;
  const point = (lat, lon) => {
    const a = lat * rad;
    const b = (lon + spin) * rad;
    return [Math.cos(a) * Math.sin(b), -Math.sin(a), Math.cos(a) * Math.cos(b)];
  };
  const tiles = [];
  for (let lat = -90; lat < 90; lat += LAT_STEP) {
    for (let lon = 0; lon < 360; lon += LON_STEP) {
      const gap = 1.2;
      const corners = [
        point(lat + gap, lon + gap),
        point(lat + gap, lon + LON_STEP - gap),
        point(lat + LAT_STEP - gap, lon + LON_STEP - gap),
        point(lat + LAT_STEP - gap, lon + gap),
      ];
      const mid = point(lat + LAT_STEP / 2, lon + LON_STEP / 2);
      if (mid[2] <= 0.05) continue;
      const lit = Math.max(0, mid[0] * LIGHT[0] + mid[1] * LIGHT[1] + mid[2] * LIGHT[2]);
      const i = lat / LAT_STEP + (lon / LON_STEP) * 7;
      const hue = HUES[((i % HUES.length) + HUES.length) % HUES.length];
      // A tile facing the light straight on flashes white.
      const flash = lit > 0.93;
      const fill = flash ? "#ffffff" : `hsl(${hue} ${55 + lit * 30}% ${22 + lit * 62}%)`;
      tiles.push({ z: mid[2], fill, pts: corners.map(([x, y]) => `${(60 + x * BALL_R).toFixed(1)},${(64 + y * BALL_R).toFixed(1)}`).join(" ") });
    }
  }
  return tiles.sort((a, b) => a.z - b.z);
}

function DiscoBall() {
  const [spin, setSpin] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setSpin((s) => (s + 3) % 360), 50);
    return () => clearInterval(id);
  }, []);
  return (
    <svg viewBox="0 0 120 120" width="1em" height="1em" aria-hidden="true" className="disco-ball">
      {/* A soft shadow under the ball, and the cap it hangs from. */}
      <ellipse cx="60" cy="114" rx="30" ry="4" fill="#000" opacity="0.25" />
      <rect x="53" y="10" width="14" height="10" rx="3" fill="#c9c9d6" stroke="#1b1b2f" strokeWidth="3" />
      <circle cx="60" cy="9" r="4" fill="none" stroke="#1b1b2f" strokeWidth="3" />
      <circle cx="60" cy="64" r={BALL_R} fill="#1d2a5c" />
      {ballTiles(spin).map((t, i) => (
        <polygon key={i} points={t.pts} fill={t.fill} />
      ))}
      {/* Cartoon shading: a dark crescent underneath, a rim of light, a big shine. */}
      <path d="M22 78a46 46 0 0 0 84-28 40 46 0 0 1-84 28z" fill="#140f3a" opacity="0.28" />
      <path d="M100 44a46 46 0 0 1 4 26" stroke="#bfe3ff" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.8" />
      <ellipse cx="42" cy="42" rx="13" ry="8" fill="#fff" opacity="0.6" transform="rotate(-30 42 42)" />
      <circle cx="35" cy="54" r="3" fill="#fff" opacity="0.7" />
      <circle cx="60" cy="64" r={BALL_R} fill="none" stroke="#1b1b2f" strokeWidth="4" />
      <path className="disco-twinkle" d="M30 26l2.4 5.6 5.6 2.4-5.6 2.4-2.4 5.6-2.4-5.6-5.6-2.4 5.6-2.4z" fill="#fff" stroke="#1b1b2f" strokeWidth="1.5" />
      <path className="disco-twinkle t2" d="M98 22l2 4.5 4.5 2-4.5 2-2 4.5-2-4.5-4.5-2 4.5-2z" fill="#ffd23f" stroke="#1b1b2f" strokeWidth="1.5" />
      <path className="disco-twinkle t3" d="M92 94l1.6 3.6 3.6 1.6-3.6 1.6-1.6 3.6-1.6-3.6-3.6-1.6 3.6-1.6z" fill="#fff" stroke="#1b1b2f" strokeWidth="1.2" />
    </svg>
  );
}
